import { useState, type ReactNode } from 'react';
import { FileText, ListPlus, Settings, SlidersHorizontal, Upload, Users } from 'lucide-react';
import { Card, CardHeader } from '@/shared/components/ui/Card';
import { Spinner } from '@/shared/components/ui/Spinner';
import { cn } from '@/shared/utils/utils';
import { usePayrollAccess } from './api';
import { EmployeesTab } from './EmployeesTab';
import { MonthlyLinesTab } from './MonthlyLinesTab';
import { PayslipsTab } from './PayslipsTab';
import { SalarySetupTab } from './SalarySetupTab';
import { SettingsTab } from './SettingsTab';
import { UploadTab } from './UploadTab';

type TabKey = 'payslips' | 'employees' | 'lines' | 'upload' | 'setup' | 'settings';

/** Payroll: salaries, how pay is built, the month's payslips and their approval, and the spreadsheet import. */
export function PayrollWorkspace() {
  const { data: access, isLoading } = usePayrollAccess();
  const [tab, setTab] = useState<TabKey>('payslips');
  if (isLoading) return <div className="flex justify-center p-12"><Spinner /></div>;
  if (!access?.canRead) return <p className="p-8 text-center text-on-surface-variant">Payroll is for HR and Accounts. You do not have access.</p>;

  const tabs: { key: TabKey; label: string; icon: ReactNode; show: boolean }[] = [
    { key: 'payslips', label: 'Payslips', icon: <FileText className="mr-2 h-4 w-4" />, show: true },
    { key: 'employees', label: 'Employee salaries', icon: <Users className="mr-2 h-4 w-4" />, show: true },
    { key: 'lines', label: 'Monthly lines', icon: <ListPlus className="mr-2 h-4 w-4" />, show: true },
    { key: 'upload', label: 'Upload sheet', icon: <Upload className="mr-2 h-4 w-4" />, show: access.canManage },
    { key: 'setup', label: 'Salary setup', icon: <SlidersHorizontal className="mr-2 h-4 w-4" />, show: true },
    { key: 'settings', label: 'Settings', icon: <Settings className="mr-2 h-4 w-4" />, show: true },
  ];

  return (
    <Card className="col-span-full rounded-xl border border-outline shadow-sm">
      <CardHeader
        title={<span className="flex items-center gap-2 text-xl font-bold text-on-surface">Payroll</span>}
        subtitle={access.canManage ? 'Set salaries, generate the month, send it for approval and share payslips.' : 'View payslips and runs. Changes are made by HR.'}
        className="border-b border-outline pb-4"
      />
      <div className="flex flex-wrap gap-3 border-b border-outline bg-surface-variant px-6 py-2">
        {tabs.filter((t) => t.show).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn('flex items-center rounded-md px-4 py-2 text-sm font-medium transition-colors', tab === t.key ? 'bg-primary text-on-primary' : 'text-on-surface-variant hover:text-on-surface')}
          >
            {t.icon}{t.label}
          </button>
        ))}
      </div>
      {tab === 'payslips' && <PayslipsTab access={access} />}
      {tab === 'employees' && <EmployeesTab access={access} />}
      {tab === 'lines' && <MonthlyLinesTab access={access} />}
      {tab === 'upload' && access.canManage && <UploadTab />}
      {tab === 'setup' && <SalarySetupTab access={access} />}
      {tab === 'settings' && <SettingsTab access={access} />}
    </Card>
  );
}
